// Sistema de toasts efímeros. Se monta una vez en App.tsx y los
// componentes piden mostrar mensajes vía el hook useToast().
//
// Una confirmación de guardado no implica confirmación del servidor.
// Mostrar un solo aviso; los problemas de sincronización tienen prioridad.

import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  ttl: number;
  key?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  toasts: Toast[];
  show: (input: Omit<Toast, 'id' | 'ttl'> & { ttl?: number }) => void;
  dismiss: (id: number | string) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  show: (input) => {
    if (input.kind === 'success' && get().toasts.some((toast) => toast.key === 'sync')) return;
    const id = nextId++;
    const ttl = input.ttl ?? 3000;
    set({ toasts: [{ ...input, id, ttl }] });
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, ttl);
    }
  },
  dismiss: (id) => {
    set((s) => {
      const toasts = s.toasts.filter((t) => (typeof id === 'string' ? t.key !== id : t.id !== id));
      return toasts.length === s.toasts.length ? s : { toasts };
    });
  },
}));

/** Hook cómodo para los componentes: muestra un toast en una línea. */
export function useToast() {
  const show = useToastStore((s) => s.show);
  return { show };
}
